/**
 * ListQueryBuilder
 *
 * New standard list query builder with fluent API for the Knex-based endpoints.
 * Uses `filters[field][op]` format instead of `where[field][op]`.
 *
 * Both terminal methods (`.get()` and `.getInfinite()`) default
 * `placeholderData: keepPreviousData` so list UIs don't flash a skeleton when
 * filters/search/sort change — the previous page stays rendered while the new
 * one fetches. Callers don't need to pass it explicitly; pass
 * `placeholderData: undefined` if you specifically want loading-state behavior.
 *
 * @example
 * ```tsx
 * const { data, fetchNextPage } = usePlacesListQuery()
 *   .filters({ type: "HOUSE,CONDO", status: "active" })
 *   .search("los angeles")
 *   .sort("-createdAt")
 *   .includes("members,managers,memberships")
 *   .take(20)
 *   .getInfinite();
 * ```
 */

import { stringifyQuery } from "@birdwatch/common";
import type {
  InfiniteData,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueries,
  useQuery,
} from "@tanstack/react-query";

import { useSessionToken } from "./use-session-token";
import { getClientConfig } from "../types";
import { nextPageOffset } from "./next-page-offset";
import { listQueryKey } from "./query-keys";
import type { QueryBuilder } from "./types";
import { buildRequestHeaders } from "./request-headers";

// ============================================================================
// Types
// ============================================================================

/**
 * Filter value can be a simple string or an object with operators
 * Simple string: "value" or "value1,value2" (auto-converts to `in`)
 * Operator object: { gte: "2024-01-01", lte: "2024-12-31" }
 */
export type FilterValue = string | Record<string, string | undefined>;

export interface ListQueryParams {
  take?: number;
  skip?: number;
  search?: string;
  sort?: string;
  filters?: Record<string, FilterValue | undefined>;
  includes?: string;
  withCount?: boolean;
  countOnly?: boolean;
  aggregates?: string;
  /**
   * Endpoint-specific top-level query params (e.g. `grouped`), serialized verbatim alongside the
   * canonical ones. The generic escape hatch for a list route that declares a param the canonical
   * set does not cover — it keeps this builder from growing one bespoke method per resource.
   */
  extra?: Record<string, string | number | boolean>;
}

export interface ListResult<T> {
  data: T[];
  extra: {
    hasNextPage: boolean;
    total?: number;
    aggregates?: Record<string, number>;
  };
}

export type ListQueryOptions<TData> = Omit<
  UseQueryOptions<TData, Error>,
  "queryKey" | "queryFn"
>;

export type ListInfiniteQueryOptions<TData> = Omit<
  UseInfiniteQueryOptions<TData, Error, InfiniteData<TData>>,
  "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
>;

/**
 * Unwrapped query result for direct access to data and extra.
 */
export interface UnwrappedListQueryResult<T>
  extends Omit<UseQueryResult<ListResult<T>, Error>, "data"> {
  data: T[] | undefined;
  extra: ListResult<T>["extra"] | undefined;
}

// ============================================================================
// Fetch Helper
// ============================================================================

async function fetchList<T>(
  url: string,
  token: string | null,
): Promise<ListResult<T>> {
  const response = await fetch(url, {
    headers: buildRequestHeaders(token),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `Request failed: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// ListQueryBuilder Class
// ============================================================================

/**
 * New List Query Builder for Knex-based endpoints.
 *
 * Uses the new standard:
 * - `filters[field][op]` instead of `where[field][op]`
 * - `includes` instead of `embed`
 * - `take`/`skip` instead of `limit`/`offset`
 *
 * @template TItem - The item type in the data array
 * @template TResponse - The full response type
 * @template TFilters - The filter fields type (defaults to untyped Record)
 */
export class ListQueryBuilder<
  TItem,
  TResponse = ListResult<TItem>,
  TFilters extends Record<string, FilterValue | undefined> = Record<
    string,
    FilterValue | undefined
  >,
> implements QueryBuilder<ListResult<TItem>>
{
  private _resource: string;
  private _path: string;
  private _params: ListQueryParams;

  constructor(resource: string, path: string, params: ListQueryParams = {}) {
    this._resource = resource;
    this._path = path;
    this._params = params;
  }

  // ==========================================================================
  // Fluent Methods
  // ==========================================================================

  /**
   * Add filter conditions.
   *
   * Values with commas are automatically converted to `in` operator.
   *
   * @example
   * ```tsx
   * .filters({ type: "HOUSE,CONDO", status: "active" })
   * // -> filters[type][in]=HOUSE,CONDO&filters[status]=active
   * ```
   */
  filters(
    filters: Partial<TFilters>,
  ): ListQueryBuilder<TItem, TResponse, TFilters> {
    return new ListQueryBuilder(this._resource, this._path, {
      ...this._params,
      filters: { ...this._params.filters, ...filters },
    });
  }

  /**
   * Add search term.
   *
   * @example
   * ```tsx
   * .search("los angeles")
   * // -> search=los%20angeles
   * ```
   */
  search(
    term: string | undefined,
  ): ListQueryBuilder<TItem, TResponse, TFilters> {
    return new ListQueryBuilder(this._resource, this._path, {
      ...this._params,
      search: term,
    });
  }

  /**
   * Set sort order.
   *
   * Prefix with `-` for descending.
   *
   * @example
   * ```tsx
   * .sort("-createdAt")
   * // -> sort=-createdAt
   * ```
   */
  sort(
    field: string | undefined,
  ): ListQueryBuilder<TItem, TResponse, TFilters> {
    return new ListQueryBuilder(this._resource, this._path, {
      ...this._params,
      sort: field,
    });
  }

  /**
   * Specify relations to include.
   *
   * @example
   * ```tsx
   * .includes("members,managers,memberships")
   * // -> includes=members,managers,memberships
   * ```
   */
  includes(
    fields: string | undefined,
  ): ListQueryBuilder<TItem, TResponse, TFilters> {
    return new ListQueryBuilder(this._resource, this._path, {
      ...this._params,
      includes: fields,
    });
  }

  /**
   * Set number of records to return.
   *
   * Use `take(-1)` to fetch all records (up to 100,000 max).
   *
   * @example
   * ```tsx
   * .take(20)
   * // -> take=20
   *
   * // Fetch all records
   * .take(-1)
   * // -> take=-1 (returns up to 100,000 records)
   * ```
   */
  take(n: number): ListQueryBuilder<TItem, TResponse, TFilters> {
    return new ListQueryBuilder(this._resource, this._path, {
      ...this._params,
      take: n,
    });
  }

  /**
   * Set number of records to skip.
   *
   * @example
   * ```tsx
   * .skip(40)
   * // -> skip=40
   * ```
   */
  skip(n: number): ListQueryBuilder<TItem, TResponse, TFilters> {
    return new ListQueryBuilder(this._resource, this._path, {
      ...this._params,
      skip: n,
    });
  }

  /**
   * Include total count in response.
   *
   * @example
   * ```tsx
   * .withCount()
   * // -> withCount=true
   * ```
   */
  withCount(): ListQueryBuilder<TItem, TResponse, TFilters> {
    return new ListQueryBuilder(this._resource, this._path, {
      ...this._params,
      withCount: true,
    });
  }

  /**
   * Return only count, no data.
   *
   * @example
   * ```tsx
   * .countOnly()
   * // -> countOnly=true
   * ```
   */
  countOnly(): ListQueryBuilder<TItem, TResponse, TFilters> {
    return new ListQueryBuilder(this._resource, this._path, {
      ...this._params,
      countOnly: true,
    });
  }

  /**
   * Request aggregate calculations on the full dataset.
   *
   * Aggregates are calculated across all matching records (ignoring pagination)
   * and returned in the `extra.aggregates` field.
   *
   * @example
   * ```tsx
   * .withAggregates("totalValue")
   * // -> withAggregates=totalValue
   * // Response: { extra: { aggregates: { totalValue: 12500 } } }
   * ```
   */
  withAggregates(fields: string): ListQueryBuilder<TItem, TResponse, TFilters> {
    return new ListQueryBuilder(this._resource, this._path, {
      ...this._params,
      aggregates: fields,
    });
  }

  /**
   * Add endpoint-specific top-level query params.
   *
   * Merged with (and overriding) any previously-set extras, and serialized exactly as given —
   * `.query({ grouped: true })` produces `?grouped=true`. Use this for a param a specific list
   * route declares; the canonical `take` / `skip` / `sort` / `filters` / `includes` set has its
   * own methods and must go through them.
   *
   * @example
   * ```tsx
   * useGetNotificationsList().query({ grouped: true }).take(20).getInfinite()
   * ```
   */
  query(
    extra: Record<string, string | number | boolean>,
  ): ListQueryBuilder<TItem, TResponse, TFilters> {
    return new ListQueryBuilder(this._resource, this._path, {
      ...this._params,
      extra: { ...this._params.extra, ...extra },
    });
  }

  // ==========================================================================
  // URL Building
  // ==========================================================================

  private buildQueryString(extraSkip?: number): string {
    const params: Record<string, unknown> = {};

    if (this._params.take !== undefined) {
      params.take = this._params.take;
    }

    // Use extraSkip for infinite query pagination
    const skipValue = extraSkip ?? this._params.skip;
    if (skipValue !== undefined && skipValue > 0) {
      params.skip = skipValue;
    }

    if (this._params.search?.trim()) {
      params.search = this._params.search.trim();
    }

    if (this._params.sort) {
      params.sort = this._params.sort;
    }

    if (this._params.includes) {
      params.includes = this._params.includes;
    }

    if (this._params.withCount) {
      params.withCount = true;
    }

    if (this._params.countOnly) {
      params.countOnly = true;
    }

    if (this._params.aggregates) {
      params.withAggregates = this._params.aggregates;
    }

    // Endpoint-specific params, before `filters` so a stray key cannot shadow the canonical set.
    for (const [key, value] of Object.entries(this._params.extra ?? {})) {
      params[key] = value;
    }

    // Build filters object: filters[field] or filters[field][op]
    if (this._params.filters) {
      const filters: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(this._params.filters)) {
        if (value === undefined || value === "") continue;

        if (typeof value === "string") {
          // Comma-separated values -> filters[field][in]=value
          // Single value -> filters[field]=value
          if (value.includes(",")) {
            filters[key] = { in: value };
          } else {
            filters[key] = value;
          }
        } else {
          // Operator object: { in: "a,b,c", gte: "2024-01-01" }
          const ops: Record<string, string> = {};
          for (const [op, opValue] of Object.entries(value)) {
            if (opValue !== undefined && opValue !== "") {
              ops[op] = opValue;
            }
          }
          if (Object.keys(ops).length > 0) {
            filters[key] = ops;
          }
        }
      }
      if (Object.keys(filters).length > 0) {
        params.filters = filters;
      }
    }

    return stringifyQuery(params);
  }

  toURL(extraSkip?: number): string {
    const config = getClientConfig();
    const queryString = this.buildQueryString(extraSkip);
    const path = `${config.baseUrl}/${this._resource}${this._path}`;
    return queryString ? `${path}?${queryString}` : path;
  }

  // ==========================================================================
  // Query Key
  // ==========================================================================

  getQueryKey(): readonly unknown[] {
    return listQueryKey(this._resource, this._path, this._params);
  }

  /**
   * Fetch data from the server.
   * Used by useMultipleQueries and usePrefetch.
   *
   * @param token - Authentication token (null for unauthenticated requests)
   * @param extraSkip - Per-page offset for infinite pagination
   * @returns Promise resolving to list result
   */
  fetch(token: string | null, extraSkip?: number): Promise<ListResult<TItem>> {
    return fetchList<TItem>(this.toURL(extraSkip), token);
  }

  // ==========================================================================
  // Terminal Methods - TanStack Query Integration
  // ==========================================================================

  /**
   * Execute query with TanStack Query.
   *
   * Defaults `placeholderData: keepPreviousData` so the previous list stays
   * visible while filters/search/sort change instead of flashing a skeleton.
   * Pass `placeholderData: undefined` to opt out.
   *
   * @example
   * ```tsx
   * const { data, extra, isLoading } = usePlacesListQuery()
   *   .filters({ status: "active" })
   *   .take(20)
   *   .get();
   * ```
   */
  get(options?: ListQueryOptions<TResponse>): UnwrappedListQueryResult<TItem> {
    const getToken = useSessionToken();
    const url = this.toURL();
    const queryKey = this.getQueryKey();

    const queryFn = async (): Promise<TResponse> => {
      const token = await getToken();
      return fetchList<TItem>(url, token) as Promise<TResponse>;
    };

    const result = useQuery({
      queryKey,
      queryFn,
      placeholderData: keepPreviousData,
      ...options,
    });

    // Unwrap the response
    const responseData = result.data as ListResult<TItem> | undefined;
    return {
      ...result,
      data: responseData?.data,
      extra: responseData?.extra,
    } as UnwrappedListQueryResult<TItem>;
  }

  /**
   * Execute query with infinite scroll support.
   *
   * Defaults `placeholderData: keepPreviousData` so existing pages stay
   * rendered while filters/search/sort change — preserves scroll position
   * and avoids skeleton flicker. Pass `placeholderData: undefined` to opt out.
   *
   * @example
   * ```tsx
   * const { data, fetchNextPage, hasNextPage } = usePlacesListQuery()
   *   .filters({ status: "active" })
   *   .includes("members,managers")
   *   .take(20)
   *   .getInfinite();
   * ```
   */
  getInfinite(
    options?: ListInfiniteQueryOptions<ListResult<TItem>>,
  ): UseInfiniteQueryResult<InfiniteData<ListResult<TItem>>, Error> {
    const getToken = useSessionToken();
    const queryKey = [...this.getQueryKey(), "infinite"] as const;
    const pageSize = this._params.take ?? 20;

    return useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam }) => {
        const token = await getToken();
        const url = this.toURL(pageParam as number);
        return fetchList<TItem>(url, token);
      },
      initialPageParam: 0 as number,
      getNextPageParam: nextPageOffset,
      placeholderData: keepPreviousData,
      ...options,
    });
  }
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create a new list query builder.
 *
 * @param resource - Resource name (e.g., "places")
 * @param path - Path to the list endpoint (e.g., "/list")
 * @returns ListQueryBuilder instance
 *
 * @example
 * ```tsx
 * export function usePlacesListQuery() {
 *   return useListQuery<PlaceListItem, PlaceListResponse>("places", "/list");
 * }
 *
 * // With typed filters:
 * export function useUsersListQuery() {
 *   return useListQuery<UserListItem, UserListResponse, UsersListFilters>("users", "/list");
 * }
 * ```
 */
export function useListQuery<
  TItem,
  TResponse = ListResult<TItem>,
  TFilters extends Record<string, FilterValue | undefined> = Record<
    string,
    FilterValue | undefined
  >,
>(
  resource: string,
  path: string,
): ListQueryBuilder<TItem, TResponse, TFilters> {
  return new ListQueryBuilder(resource, path);
}

// ============================================================================
// Multiple Queries Hook
// ============================================================================

/**
 * Unwrapped result for list queries.
 * Extends UseQueryResult but with unwrapped data and extra fields.
 */
export interface UnwrappedMultipleListQueryResult<T>
  extends Omit<UseQueryResult<ListResult<T>, Error>, "data"> {
  data: T[] | undefined;
  extra: ListResult<T>["extra"] | undefined;
}

/**
 * Unwrapped result for detail/custom queries.
 * Extends UseQueryResult but with unwrapped data field.
 */
export interface UnwrappedMultipleDetailQueryResult<T>
  extends Omit<UseQueryResult<{ data: T }, Error>, "data"> {
  data: T | undefined;
}

/**
 * Execute multiple queries in parallel.
 * Accepts any QueryBuilder (list, detail, or custom).
 * Returns unwrapped results matching the input order.
 *
 * @param builders - Array of QueryBuilder instances (list, detail, or custom)
 * @param options - Optional query options applied to all queries
 * @returns Array of unwrapped query results matching the input order
 *
 * @example
 * ```tsx
 * // Mix list, detail, and custom queries
 * const [places, orderDetail, visits] = useMultipleQueries([
 *   usePlacesListQuery().filters({ status: "active" }).take(10),
 *   useOrdersDetail(orderId),
 *   useOrdersVisits({ orderId }).query({ take: 10 }),
 * ]);
 *
 * // Access data directly
 * places.data // T[] | undefined (list)
 * places.extra // { hasNextPage, total?, aggregates? } | undefined
 * orderDetail.data // Order | undefined (detail)
 * visits.data // Visit[] | undefined (custom)
 * ```
 */
export function useMultipleQueries<T extends QueryBuilder<any>[]>(
  builders: [...T],
  options?: Omit<ListQueryOptions<any>, "queryKey" | "queryFn">,
): {
  [K in keyof T]: T[K] extends ListQueryBuilder<infer TItem, any, any>
    ? UnwrappedMultipleListQueryResult<TItem>
    : T[K] extends QueryBuilder<infer TData>
      ? TData extends ListResult<infer TListItem>
        ? UnwrappedMultipleListQueryResult<TListItem>
        : TData extends { data: infer TDetail }
          ? UnwrappedMultipleDetailQueryResult<TDetail>
          : never
      : never;
} {
  const getToken = useSessionToken();

  const results = useQueries({
    queries: builders.map((builder) => ({
      queryKey: builder.getQueryKey(),
      queryFn: async () => {
        const token = await getToken();
        return builder.fetch(token);
      },
      ...options,
    })),
  });

  // Unwrap each result based on response shape
  return results.map((result) => {
    const responseData = result.data;

    // Handle list response: { data: T[], extra: {...} }
    if (responseData && "extra" in responseData) {
      return {
        ...result,
        data: responseData.data,
        extra: responseData.extra,
      };
    }

    // Handle detail/custom response: { data: T }
    if (responseData && "data" in responseData) {
      return {
        ...result,
        data: responseData.data,
      };
    }

    // No data yet
    return {
      ...result,
      data: undefined,
    };
  }) as any;
}
